import React, { useEffect, useMemo, useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { AlignItems, Box, Display, FlexDirection } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import type { MenuPreferences } from '@library/api-menu-experience'
import {
  CategoryBranding,
  CollectionDescription,
  CollectionTitle,
  RecipeCount,
} from '@library/menu-page'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import type {
  BrowseSelectionState,
  ViewRecipeCollectionEvent,
} from 'routes/Menu/MenuExperience/MenuPage/types'
import { useMerchandisingHeader } from 'routes/Menu/MenuRecipesPage/CollectionHeader'
import { useBasketDelivery } from 'routes/Menu/domains/basket/internal/delivery'
import { CollectionId } from 'routes/Menu/domains/collections'
import { getBasketMenuUuid, getBasketRecipesCount } from 'selectors/basket'

import { FeaturedRangeCollectionHeader } from './CollectionHeader/FeaturedRangeCollectionHeader'
import { RecipeGrid } from './RecipeGrid/RecipeGrid'
import {
  getMerchandisingHeaderType,
  useLoadMerchBannerTracking,
} from './useLoadMerchBannerTracking'

type CollectionScreenProps = {
  browseSelectionState?: BrowseSelectionState
  clearBrowseSelectionState?: () => void
  menuPreferences?: MenuPreferences
  userStatus?: string
  isFlexOrder?: boolean
}

const CATEGORY_DESCRIPTION: Record<string, { title: string; subtitle: string }> = {
  default: {
    title: "Sorted by what's relevant to you.",
    subtitle: "Based on what you've had before.",
  },
  [CollectionId.Recommendations]: {
    title: "Based on what you've had before.",
    subtitle: "We think you'll love these recipes.",
  },
}

export const CollectionScreen = ({
  browseSelectionState = null,
  clearBrowseSelectionState = () => {},
  menuPreferences,
  userStatus = '',
  isFlexOrder = false,
}: CollectionScreenProps) => {
  const currentCollectionId = useCurrentCollectionId()
  const lastTrackedCollectionIdRef = useRef<string | null>(null)

  const collection = useCollection(currentCollectionId)
  const { shortTitle, recipes } = collection ?? {
    shortTitle: '',
    recipes: [],
  }
  const delivery = useBasketDelivery()
  const menuUuid = useSelector(getBasketMenuUuid) ?? ''
  const basketCount = useSelector(getBasketRecipesCount)

  const merchandisingHeader = useMerchandisingHeader(currentCollectionId)

  // Category branding replaces the normal title area later in this component,
  // so only the other featured-range header types count as visible banners here.
  const hasMerchandisingBanner =
    Boolean(merchandisingHeader) && merchandisingHeader?.type !== 'category-branding'

  const showAccountOnHoldAlert = userStatus === 'onhold'

  const { title, subtitle } =
    CATEGORY_DESCRIPTION[currentCollectionId] ?? CATEGORY_DESCRIPTION.default

  const isMerchandisingHeadersLoading = !collection
  const isMerchandisingBannerVisible =
    hasMerchandisingBanner && !isFlexOrder && !showAccountOnHoldAlert
  const merchHeaderType = useMemo(() => {
    if (isFlexOrder || showAccountOnHoldAlert) {
      return null
    }

    return getMerchandisingHeaderType(merchandisingHeader)
  }, [merchandisingHeader, isFlexOrder, showAccountOnHoldAlert])

  useEffect(() => {
    if (isMerchandisingHeadersLoading) {
      return
    }

    datadogRum?.addAction('view_collection_screen', {
      deliveryDate: delivery.date,
      collectionId: currentCollectionId,
      recipeCount: recipes.length,
      merchandisingBannerShown: isMerchandisingBannerVisible,
    })
  }, [
    currentCollectionId,
    recipes.length,
    isMerchandisingBannerVisible,
    delivery.date,
    isMerchandisingHeadersLoading,
  ])

  useEffect(() => {
    if (!collection?.slug) {
      return
    }

    if (lastTrackedCollectionIdRef.current === currentCollectionId) {
      return
    }

    queueTrackingEvent<ViewRecipeCollectionEvent>({
      action: 'view_recipe_collection',
      properties: {
        collection_id: currentCollectionId,
        collection_slug: collection.slug,
        menu_uuid: menuUuid,
        basket_count: basketCount,
        original_order: collection.recipes.map(({ id }) => id).join(','),
        ...(merchHeaderType && {
          merch_header_type: merchHeaderType,
        }),
        ...(browseSelectionState?.childCollectionId === currentCollectionId
          ? { parent_collection_id: browseSelectionState.parentCollectionId }
          : {}),
      },
    })

    lastTrackedCollectionIdRef.current = currentCollectionId
    clearBrowseSelectionState()
  }, [
    basketCount,
    browseSelectionState,
    clearBrowseSelectionState,
    collection,
    currentCollectionId,
    merchHeaderType,
    menuUuid,
  ])

  useLoadMerchBannerTracking({
    collectionId: currentCollectionId,
    collectionSlug: collection?.slug,
    isMerchandisingBannerVisible,
    isMerchandisingHeadersLoading,
    menuUuid,
    merchandisingHeader,
  })

  const categoryBranding =
    merchandisingHeader?.type === 'category-branding' ? merchandisingHeader : null

  return (
    <>
      <FeaturedRangeCollectionHeader
        showAccountOnHoldAlert={showAccountOnHoldAlert}
        isFlexOrder={isFlexOrder}
        merchandisingHeader={merchandisingHeader}
      />
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.Center}
      >
        {categoryBranding ? (
          // branding replaces category title and description
          <CategoryBranding contentBlock={categoryBranding} recipeCount={recipes.length}>
            <RecipeGrid menuPreferences={menuPreferences} />
          </CategoryBranding>
        ) : (
          <Box maxWidth="1600px" width="100%">
            <Box paddingH={[3, 3, 0]} paddingBottom={2}>
              {!isMerchandisingBannerVisible && <CollectionTitle text={shortTitle} />}
              <CollectionDescription title={title} subtitle={subtitle} />
              <RecipeCount count={recipes.length} />
            </Box>
            <RecipeGrid menuPreferences={menuPreferences} />
          </Box>
        )}
      </Box>
    </>
  )
}
